import { FC, useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getAllMembers, getFinancialStats } from '../../api/memberApi';
import type { Member } from '../../types/member';
import type { FinancialHealthStats } from '../../types/financialStats';
import { useDataSource } from '../../contexts/DataSourceContext';
import { useUser } from '../../context/UserContext';
import { canAccessRole } from '../../services/userService';
import { FiTarget, FiBarChart2, FiUsers, FiCheckCircle } from 'react-icons/fi';
import RoleSelector from './RoleSelector';
import ActionList from './ActionList';
import RecentActivitySection from './RecentActivitySection';
import AssistantCard from './AssistantCard';
import { useInsightsData } from '../../hooks/useInsightsData';
import { fetchRecommendationPerformance, buildPerformanceMap, type RecommendationPerformanceResponse } from '../../services/recommendationPerformanceService';
import { fetchRecommendationOutcomes, buildOutcomeMap, type RecommendationOutcomesResponse } from '../../services/recommendationOutcomesService';

// Define role types
export type UserRole = 'CEO' | 'VP of Lending' | 'Loan Advisor';

// Define the props for the ActionsPanel component
interface ActionsPanelProps {}

const ActionsPanel: FC<ActionsPanelProps> = () => {
  // Get the active persona from context
  const { activePersona, setActivePersona, allPersonas } = useUser();
  
  // State for role selection - initialize with the active persona's role
  const [selectedRole, setSelectedRole] = useState<UserRole>(activePersona.role);
  
  // Combined useEffect to manage role/persona synchronization and access control
  useEffect(() => {
    // Skip effect logic during the initial render
    const isInitialRender = selectedRole === activePersona.role;
    
    if (!isInitialRender) {
      // Check if the selected role is accessible to the current persona
      if (canAccessRole(activePersona.role, selectedRole)) {
        // If accessible, update the persona to match the selected role
        const personaForRole = allPersonas.find(p => p.role === selectedRole);
        if (personaForRole) {
          setActivePersona(personaForRole);
        }
      } else {
        // If not accessible, reset the selected role to match the persona's role
        setSelectedRole(activePersona.role);
      }
    }
  }, [selectedRole, activePersona.role]);

  // Update the selected role if the active persona changes externally (like from dropdown)
  useEffect(() => {
    // Only update if they don't match
    if (selectedRole !== activePersona.role) {
      setSelectedRole(activePersona.role);
    }
  }, [activePersona]);
  
  // State for member data
  const [members, setMembers] = useState<Member[]>([]);
  const [financialData, setFinancialData] = useState<Record<string, FinancialHealthStats[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get data source from context
  const { dataSource } = useDataSource();

  // P2C: Fetch behavioral insights for data-driven recommendations
  const { data: insightsData } = useInsightsData();

  // P3.3: Fetch recommendation performance data (non-blocking)
  const [performanceData, setPerformanceData] = useState<RecommendationPerformanceResponse | null>(null);
  useEffect(() => {
    fetchRecommendationPerformance().then(({ data }) => {
      if (data) setPerformanceData(data);
    });
  }, []);

  // P3.3: Build performance lookup map (stable reference via useMemo)
  const performanceMap = useMemo(
    () => buildPerformanceMap(performanceData),
    [performanceData],
  );

  // P4A Phase 6: Fetch recommendation outcomes (non-blocking)
  const [outcomesData, setOutcomesData] = useState<RecommendationOutcomesResponse | null>(null);
  useEffect(() => {
    fetchRecommendationOutcomes().then(({ data }) => {
      if (data) setOutcomesData(data);
    });
  }, []);

  // P4A Phase 6: Build outcome lookup map (stable reference via useMemo)
  const outcomeMap = useMemo(
    () => buildOutcomeMap(outcomesData),
    [outcomesData],
  );

  // Fetch members data
  useEffect(() => {
    const fetchMemberData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Get all members (limit to 50 for performance in demo)
        const response = await getAllMembers({ pageSize: 50 });
        setMembers(response.data);
        
        // Fetch financial data for each member
        const financialDataMap: Record<string, FinancialHealthStats[]> = {};
        
        await Promise.all(
          response.data.slice(0, 20).map(async (member) => { // Limit to first 20 for performance
            try {
              const stats = await getFinancialStats(member.uuid, dataSource);
              financialDataMap[member.uuid] = stats;
            } catch (e) {
              if (import.meta.env.DEV) console.error(`Error fetching financial data for member ${member.uuid}:`, e);
              financialDataMap[member.uuid] = [];
            }
          })
        );
        
        setFinancialData(financialDataMap);
      } catch (e) {
        if (import.meta.env.DEV) console.error('Error fetching member data:', e);
        setError('Failed to load member data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMemberData();
  }, [dataSource]);

  // Get the appropriate greeting based on time of day
  const getTimeBasedGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return 'Good morning';
    } else if (hour >= 12 && hour < 18) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  };
  
  // Extract first name from full name
  const getFirstName = (fullName: string): string => {
    return fullName.split(' ')[0];
  };

  // Get the welcome message
  const greeting = getTimeBasedGreeting();
  
  // Get the first name for the assistant
  const firstName = getFirstName(activePersona.name);
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EAEDF4' }}>
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Header with Role Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-slate-700 mb-2">VeraScore Actions</h1>
            <p className="text-slate-500">
              Take automated actions to help your members achieve financial wellness
            </p>
          </div>
          
          <RoleSelector 
            selectedRole={selectedRole} 
            onRoleChange={setSelectedRole} 
          />
        </motion.div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* VeraScore Assistant Card */}
          <AssistantCard role={selectedRole} firstName={firstName} greeting={greeting} />
          
          {/* Workbench Card */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Add title based on role - Header section */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center mr-3">
                  {selectedRole === 'CEO' 
                    ? <FiTarget className="text-slate-600" />
                    : selectedRole === 'VP of Lending' 
                      ? <FiBarChart2 className="text-slate-600" />
                      : <FiUsers className="text-slate-600" />
                  }
                </div>
                <h2 className="text-2xl font-bold text-slate-700">
                  {selectedRole === 'CEO' 
                    ? 'Executive Workbench' 
                    : selectedRole === 'VP of Lending' 
                      ? 'Lending Portfolio Workbench'
                      : 'Member Engagement Workbench'
                  }
                </h2>
              </div>
              <p className="text-slate-500 ml-11">
                {selectedRole === 'CEO' 
                  ? 'High-impact opportunities that require your approval to drive institutional growth' 
                  : selectedRole === 'VP of Lending' 
                    ? 'Key lending initiatives to optimize your portfolio performance and market position'
                    : 'Personalized actions to enhance member relationships and financial wellness'
                }
              </p>
            </div>
            
            {/* Workbench content */}
            <div className="p-6 bg-white">
              {isLoading ? (
                <div className="py-8 flex justify-center">
                  <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                  <p>{error}</p>
                </div>
              ) : (
                <ActionList
                  role={selectedRole}
                  members={members}
                  financialData={financialData}
                  behavioralInsights={insightsData?.behavioral}
                  performanceMap={performanceMap}
                  outcomeMap={outcomeMap}
                />
              )}
            </div>
          </div>
          
          {/* Recent Activity Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Recent Activity Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center mr-3">
                  <FiCheckCircle className="text-slate-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-700">Recently Completed Actions</h2>
              </div>
            </div>
            
            {/* Recent Activity Content */}
            <div className="p-6 bg-white">
              <RecentActivitySection role={selectedRole} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ActionsPanel;